import { useCallback, useRef } from 'react';
import { DateTime, Duration } from 'luxon';
import { OrderType } from '../../../../clients/quipli-service/order/order';
import { OrderInputItem, OrderInputRental } from '../../../../clients/quipli-service/order/order-calculation';
import { CustomOrderPriceRequest, RentalProductPrice } from '../../../../clients/quipli-service/pricing/pricing';
import { toast } from '../../../../util/toasts';
import { Location } from '../../../../clients/quipli-service/location/location';
import { useRentalPriceCalculator } from './use-rental-price-calculator';

export interface ProductItemStateRentalRates {
  current: CustomOrderPriceRequest[];
  original: CustomOrderPriceRequest[];
}

export interface ProductItemStateRentalRatesReturn {
  updateRate: (price: RentalProductPrice, newPriceValue: number) => Promise<void>;
  resetRate: (price: RentalProductPrice) => Promise<void>;
}

export const useProductItemRentalRates = ({
  inputItem,
  customRentalRates,
  orderId,
  location,
  taxCodeId,
  cycleLength,
  isTaxInclusive,
  isOrderCycleBilled,
  onChange,
  setTaxedUnitPrice,
  setTotalTaxPercentage,
}: {
  inputItem?: OrderInputItem;
  customRentalRates: ProductItemStateRentalRates;
  orderId?: number;
  location: Location;
  taxCodeId?: string;
  cycleLength?: Duration;
  isTaxInclusive: boolean;
  isOrderCycleBilled: boolean;
  onChange?: (orderInputItem: OrderInputItem) => void;
  setTaxedUnitPrice?: (price: number) => void;
  setTotalTaxPercentage?: (percentage: number) => void;
}): ProductItemStateRentalRatesReturn => {
  const { calculateRentalPrice } = useRentalPriceCalculator({
    location,
    isTaxInclusive,
    isOrderCycleBilled,
    cycleBillingTerms: cycleLength ? { cycleLength } : undefined,
    setTaxedUnitPrice,
    setTotalTaxPercentage,
  });

  // Use ref to always get the latest inputItem
  const inputItemRef = useRef(inputItem);
  inputItemRef.current = inputItem;

  // Standardized rate update function that handles both onChange and onReset
  const updateInputItemRate = useCallback(
    async (price: RentalProductPrice, newPriceValue?: number) => {
      if (!inputItemRef.current || !onChange) {
        console.error('Missing required parameters for rate update');
        return;
      }

      const currentInputItem = inputItemRef.current;
      const currentCustomRates = (currentInputItem as any).customOrderRentalRates || [];

      // Calculate dates - use current dates from input item, not stale closure values
      const currentStartDate = (currentInputItem as OrderInputRental).start;
      const currentEndDate = (currentInputItem as OrderInputRental).end;
      const maxEndDate = currentStartDate.plus(cycleLength || { days: 0 });

      // Find current rate value
      const editableFieldVal =
        customRentalRates.current.find(
          (r) =>
            r.productId === price.productId &&
            r.durationUnit === price.durationUnit &&
            r.durationValue === price.durationValue,
        ) ??
        customRentalRates.original.find(
          (r) =>
            r.productId === price.productId &&
            r.durationUnit === price.durationUnit &&
            r.durationValue === price.durationValue,
        ) ??
        price;

      // Determine if this is a reset (no newPriceValue) or update
      const isReset = newPriceValue === undefined;
      const priceInCents = isReset ? price.originalPrice?.price || price.price : (newPriceValue || 0) * 100;

      // Build custom rental rates array for API call
      const customOrderRentalRates = isReset
        ? ([
            ...currentCustomRates.filter(
              (r: any) =>
                (!!(r as RentalProductPrice).originalPrice?.id || !!r.action) &&
                !(
                  r.productId === price.productId &&
                  r.durationUnit === price.durationUnit &&
                  r.durationValue === price.durationValue
                ),
            ),
            price.originalPrice?.id ? { ...price.originalPrice, action: 'remove', idToChange: price.id } : undefined,
          ].filter((r) => !!r) as CustomOrderPriceRequest[])
        : [
            ...currentCustomRates.filter(
              (r: any) =>
                (!!(r as RentalProductPrice).originalPrice?.id || !!r.action) &&
                !(
                  r.productId === price.productId &&
                  r.durationUnit === price.durationUnit &&
                  r.durationValue === price.durationValue
                ),
            ),
            {
              ...editableFieldVal,
              price: priceInCents,
              action:
                price.originalPrice?.price && priceInCents === price.originalPrice?.price
                  ? 'remove'
                  : price.originalPrice?.price
                  ? 'update'
                  : 'add',
              idToChange:
                price.originalPrice?.price && priceInCents === price.originalPrice?.price
                  ? price.id
                  : price.originalPrice?.price
                  ? price.id
                  : undefined,
            },
          ];

      try {
        const priceResult = await calculateRentalPrice({
          productId: currentInputItem.productId,
          orderProductId: !isNaN(Number(currentInputItem.id)) ? parseInt(currentInputItem.id) : undefined,
          orderId: orderId,
          start: currentStartDate,
          end: currentEndDate
            ? isOrderCycleBilled
              ? DateTime.min(currentEndDate, maxEndDate)
              : currentEndDate
            : maxEndDate,
          type: OrderType.Rental,
          taxCodeId: taxCodeId,
          customOrderRentalRates,
        });

        if (priceResult.error) {
          return; // Error already toasted by calculator
        }

        // Update the input item with new rates array for final state
        const finalCustomOrderRentalRates = isReset
          ? ([
              ...currentCustomRates.filter(
                (r: any) =>
                  !(
                    r.productId === price.productId &&
                    r.durationUnit === price.durationUnit &&
                    r.durationValue === price.durationValue
                  ),
              ),
              price.originalPrice?.id ? { ...price.originalPrice, action: 'remove', idToChange: price.id } : undefined,
            ].filter((r) => !!r) as CustomOrderPriceRequest[])
          : [
              ...currentCustomRates.filter(
                (r: any) =>
                  !(
                    r.productId === price.productId &&
                    r.durationUnit === price.durationUnit &&
                    r.durationValue === price.durationValue
                  ),
              ),
              {
                ...editableFieldVal,
                price: priceInCents,
                action:
                  price.originalPrice?.price && priceInCents === price.originalPrice?.price
                    ? 'remove'
                    : price.originalPrice?.price
                    ? 'update'
                    : 'add',
                idToChange:
                  price.originalPrice?.price && priceInCents === price.originalPrice?.price
                    ? price.id
                    : price.originalPrice?.price
                    ? price.id
                    : undefined,
              },
            ];

        onChange({
          ...currentInputItem,
          customPrice: isTaxInclusive ? priceResult.taxedPrice : priceResult.totalPrice,
          customOrderRentalRates: finalCustomOrderRentalRates,
        } as OrderInputRental);
      } catch (error) {
        toast('Error updating rate', 'error');
        console.error('Rate update error:', error);
      }
    },
    [
      onChange,
      orderId,
      cycleLength,
      taxCodeId,
      isTaxInclusive,
      isOrderCycleBilled,
      customRentalRates,
      calculateRentalPrice,
    ],
  );

  const updateRate = useCallback(
    (price: RentalProductPrice, newPriceValue: number) => updateInputItemRate(price, newPriceValue),
    [updateInputItemRate],
  );

  const resetRate = useCallback(
    (price: RentalProductPrice) => updateInputItemRate(price), // No newPriceValue = reset
    [updateInputItemRate],
  );

  return {
    updateRate,
    resetRate,
  };
};
